import React from "react";

import {
    Button,
    Divider,
    IconButton,
    Input,
    Sheet,
    Stack,
    Table,
    Textarea,
    Typography,
} from "@mui/joy";

import {
    AddOutlined,
    AutoAwesomeOutlined,
    ClearOutlined,
    TableViewOutlined,
} from "@mui/icons-material";
import { GenerateSpreadsheetArgs } from "../../shared/interfaces";

export interface SpreadsheetFlowProps {
    onSubmit: (data: GenerateSpreadsheetArgs) => void;
    onClose: () => void;
}

interface TemplateSpreadsheetArgValue {
    key: string;
    selectorTitle: string;
    args: GenerateSpreadsheetArgs;
}

const allTemplates: TemplateSpreadsheetArgValue[] = [
    {
        key: "submittals",
        selectorTitle: "Submittals",
        args: {
            dataToExtract: "submittals",
            columns: [
                { title: "Section", description: "Example: 08 11 13" },
                {
                    title: "Section Title",
                    description: "Example: Hollow Metal Doors and Frames",
                },
                { title: "Subsection", description: "Example: 1.3-A" },
                { title: "Type", description: "Example: Product Data" },
                {
                    title: "Description",
                    description:
                        "Example: For each type of product indicated. Include construction details, material\n" +
                        "descriptions, core descriptions, hardware reinforcements, profiles, anchors, fire-resistance\n" +
                        "rating, and finishes.\n",
                },
            ],
        },
    },
    {
        key: "as_built",
        selectorTitle: "As-builts",
        args: {
            dataToExtract: `As-builts`,
            columns: [
                { title: "Section", description: "Example: 16415" },
                {
                    title: "Section Title",
                    description: "Example: Automatic Transfer Switches",
                },
                { title: "Subsection", description: "Example: 1.3-B" },
                {
                    title: "Description",
                    description:
                        "Example: Outline, Schematic and Part Layout drawings shall be provided for each\n" +
                        "switch. Drawings shall differentiate between manufacturer-installed and field installed\n" +
                        "wiring. Show both power and control wiring. Such drawings shall be unique to the serial number of the switch they represent " +
                        "and shall include only those features of the specific switch.\n",
                },
            ],
        },
    },
    {
        key: "preinstall_meetings",
        selectorTitle: "Activities with preinstall meetings",
        args: {
            dataToExtract: `Activities that involve pre-installation meetings like coordination meetings and pre-construction meetings`,
            columns: [
                { title: "Section", description: "Example: 09 51 13" },
                { title: "Section Title", description: "Example: Acoustical Ceilings" },
                {
                    title: "Description",
                    description: "Example: Conduct conference at Project site",
                },
            ],
        },
    },
    {
        key: "as_plans",
        selectorTitle: "DOT - Plans quantities",
        args: {
            dataToExtract: `Items that include plans quantity as part of measurement.`,
            columns: [
                { title: "Section", description: "Example: Item 403" },
                {
                    title: "Section Title",
                    description: "Example: Temporary Special Shoring",
                },
                {
                    title: "Measurement",
                    description:
                        "Example: This Item will be measured by the square foot of surface area of a " +
                        "vertical plane at the face of the shoring between the top of the ground being supported and " +
                        "the minimum protection grade line shown on the plans. If no minimum protection grade is shown " +
                        "on the plans, the lowest required excavated elevation will be used. Shoring projecting above " +
                        "the level of the ground being supported will not be measured. When excavation techniques " +
                        "(e.g., sloped cuts or benching) are used to provide the necessary protection, the surface " +
                        "area for payment will be calculated based on the area described by a vertical plane " +
                        "adjacent to the structure.",
                },
            ],
        },
    },
    {
        key: "custom",
        selectorTitle: "Custom ...",
        args: {
            dataToExtract: "",
            columns: [{ title: "", description: "" }],
        },
    },
];

const styles = {
    templateSelectorContainer: { p: 2 },
    columnDescriptionContainer: { width: "100%" },
    columnDescriptionTextarea: { flexGrow: 1 },
    columnNameHeading: { width: "30%" },
};

export default function SpreadsheetFlow(props: SpreadsheetFlowProps) {
    const [spreadsheetArgs, setSpreadsheetArgs] = React.useState<GenerateSpreadsheetArgs>(
        allTemplates[0].args,
    );

    const [customSpreadsheetArgs, setCustomSpreadsheetArgs] =
        React.useState<GenerateSpreadsheetArgs>({
            dataToExtract: "",
            columns: [{ title: "", description: "" }],
        });

    const [selectedTemplate, setSelectedTemplate] = React.useState<string | null>(
        allTemplates[0].key,
    );

    const handleTemplateChange = (newValue: string | null) => {
        if (selectedTemplate === "custom") {
            // Save the current custom data before switching templates
            setCustomSpreadsheetArgs(spreadsheetArgs);
        }

        setSelectedTemplate(newValue || "");

        if (newValue === "custom") {
            // Restore the custom data
            setSpreadsheetArgs(customSpreadsheetArgs);
        } else {
            // Find the selected template from the array
            const selectedTemplateArgs = allTemplates.find((template) => template.key === newValue);
            if (selectedTemplateArgs) {
                setSpreadsheetArgs(selectedTemplateArgs.args);
            }
        }
    };

    const handleTitleChange = (index: number, value: string) => {
        const newColumns = [...spreadsheetArgs.columns];
        newColumns[index].title = value;
        const updatedArgs = { ...spreadsheetArgs, columns: newColumns };
        setSpreadsheetArgs(updatedArgs);

        if (selectedTemplate === "custom") {
            setCustomSpreadsheetArgs(updatedArgs);
        }
    };

    const handleDescriptionChange = (index: number, value: string) => {
        const newColumns = [...spreadsheetArgs.columns];
        newColumns[index].description = value;
        setSpreadsheetArgs({ ...spreadsheetArgs, columns: newColumns });
    };

    const handleRemoveColumn = (index: number) => {
        const newColumns = spreadsheetArgs.columns.filter((_, i) => i !== index);
        setSpreadsheetArgs({ ...spreadsheetArgs, columns: newColumns });
    };

    const handleAddColumn = () => {
        setSpreadsheetArgs({
            ...spreadsheetArgs,
            columns: [...spreadsheetArgs.columns, { title: "", description: "" }],
        });
    };

    const handleDataToExtractChange = (value: string) => {
        setSpreadsheetArgs({ ...spreadsheetArgs, dataToExtract: value });
    };

    return (
        <Stack spacing={2}>
            <Typography level="h4" color="primary" startDecorator={<TableViewOutlined />}>
                Generate spreadsheet
            </Typography>

            <Stack spacing={4}>
                <Sheet variant="soft" sx={styles.templateSelectorContainer}>
                    <Stack spacing={1}>
                        <Typography level="body-sm">
                            Marvin will analyze your project and build a spreadsheet for you. You
                            can use one of the templates below, or you can specify your own for
                            custom analysis.
                        </Typography>

                        <Typography level="title-sm">Select template: </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            {allTemplates.map((template) => (
                                <Button
                                    size="sm"
                                    variant={
                                        selectedTemplate === template.key ? "solid" : "outlined"
                                    }
                                    color={
                                        selectedTemplate === template.key ? "primary" : "neutral"
                                    }
                                    onClick={() => handleTemplateChange(template.key)}
                                >
                                    {template.selectorTitle}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>
                </Sheet>

                <Stack spacing={1}>
                    <Typography level="title-md">
                        What information do you to extract? Marvin loves examples!
                    </Typography>
                    <Textarea
                        minRows={2}
                        value={spreadsheetArgs.dataToExtract}
                        onChange={(e) => handleDataToExtractChange(e.target.value)}
                        placeholder="Information to extract, e.g. submittals, as-built, etc ..."
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography level="title-md">Columns</Typography>
                    <Table>
                        <thead>
                            <tr>
                                <th style={styles.columnNameHeading}>
                                    <Typography>Column</Typography>
                                </th>
                                <th>
                                    <Typography>
                                        How should Marvin find this value? Give an example.
                                    </Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {spreadsheetArgs.columns.map((column: any, i: any) => (
                                <tr key={i}>
                                    <td style={{ verticalAlign: "top" }}>
                                        <Input
                                            size="sm"
                                            variant="soft"
                                            value={column.title}
                                            onChange={(e) => handleTitleChange(i, e.target.value)}
                                            placeholder="Column ..."
                                        />
                                    </td>
                                    <td>
                                        <Stack
                                            direction="row"
                                            sx={styles.columnDescriptionContainer}
                                        >
                                            <Textarea
                                                size="sm"
                                                minRows={1}
                                                variant="soft"
                                                value={column.description}
                                                onChange={(e) =>
                                                    handleDescriptionChange(i, e.target.value)
                                                }
                                                sx={styles.columnDescriptionTextarea}
                                                placeholder="Description ..."
                                            />
                                            <IconButton
                                                variant="plain"
                                                size="sm"
                                                onClick={() => handleRemoveColumn(i)}
                                            >
                                                <ClearOutlined />
                                            </IconButton>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button
                        variant="plain"
                        color="neutral"
                        size="sm"
                        startDecorator={<AddOutlined />}
                        onClick={handleAddColumn}
                    >
                        Add Column
                    </Button>
                </Stack>

                <Stack spacing={2}>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                        <Button color="neutral" variant="soft" onClick={props.onClose}>
                            Cancel
                        </Button>

                        <Button
                            variant="solid"
                            color="primary"
                            startDecorator={<AutoAwesomeOutlined />}
                            onClick={() => props.onSubmit(spreadsheetArgs)}
                        >
                            Generate spreadsheet
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
